import React, { useState } from 'react';
import { Trophy, Users, Lock, AlertCircle, X, Target, Plus, Minus, Gamepad2, Calendar } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './BRMultistep.css';

const BRMultistepCreate = ({ user, onClose, onCreated, t }) => {
  const [formData, setFormData] = useState({
    type: 'solo',
    totalRounds: 3,
    maxPlayers: 20,
    prizePool: 500,
    password: '',
    scheduledStart: '',
    control: 'manual',
    pointsConfig: {
      killPoints: 1,
      topPlacements: [
        { position: 1, points: 15 },
        { position: 2, points: 12 },
        { position: 3, points: 10 },
        { position: 4, points: 8 },
        { position: 5, points: 6 }
      ]
    }
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const typeOptions = [
    { value: 'solo', label: t('solo', 'Solo'), icon: '👤', desc: t('soloDesc', 'Individual competition') },
    { value: 'duo', label: t('duo', 'Duo'), icon: '👥', desc: t('duoDesc', '2-player teams') },
    { value: 'squad', label: t('squad', 'Squad'), icon: '👨‍👩‍👧‍👦', desc: t('squadDesc', '4-player teams') }
  ];

  const getTeamSize = () => {
    switch (formData.type) {
      case 'duo': return 2;
      case 'squad': return 4;
      default: return 1;
    }
  };

  const getMaxTeams = () => Math.floor(formData.maxPlayers / getTeamSize());

  const addPlacement = () => {
    const placements = formData.pointsConfig.topPlacements;
    const nextPos = placements.length > 0 ? placements[placements.length - 1].position + 1 : 1;
    const nextPoints = placements.length > 0 ? Math.max(0, placements[placements.length - 1].points - 2) : 10;
    setFormData({
      ...formData,
      pointsConfig: {
        ...formData.pointsConfig,
        topPlacements: [...placements, { position: nextPos, points: nextPoints }]
      }
    });
  };

  const removePlacement = (index) => {
    if (formData.pointsConfig.topPlacements.length <= 1) return;
    const placements = [...formData.pointsConfig.topPlacements];
    placements.splice(index, 1);
    setFormData({
      ...formData,
      pointsConfig: { ...formData.pointsConfig, topPlacements: placements }
    });
  };

  const updatePlacement = (index, field, value) => {
    const placements = [...formData.pointsConfig.topPlacements];
    placements[index] = { ...placements[index], [field]: parseInt(value) || 0 };
    setFormData({
      ...formData,
      pointsConfig: { ...formData.pointsConfig, topPlacements: placements }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.maxPlayers < 2) {
      toast.error(t('minPlayersRequired', 'Minimum 2 players required'));
      return;
    }
    if (formData.type !== 'solo' && formData.maxPlayers % getTeamSize() !== 0) {
      toast.error(t('playersNotDivisible', 'Max players must be divisible by team size'));
      return;
    }
    setShowConfirm(true);
  };

  const confirmCreate = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/br-multistep/create', formData);
      toast.success(t('brMultistepCreated', 'BR Multistep tournament created!'));
      if (onCreated) {
        onCreated(response.data.tournament, response.data.userPoints);
      }
      onClose();
    } catch (error) {
      console.error('Error creating BR Multistep tournament:', error);
      toast.error(error.response?.data?.message || t('errorCreating', 'Error creating tournament'));
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="br-multistep-modal-overlay">
      <div className="br-multistep-modal br-multistep-create-modal">
        <div className="br-multistep-modal-header">
          <div className="br-multistep-modal-title">
            <Target className="w-6 h-6 text-purple-400" />
            <h2>{t('createBRMultistep', 'Create BR Multistep Tournament')}</h2>
          </div>
          <button className="br-multistep-modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="br-multistep-create-form">
          {/* Tournament Type */}
          <div className="br-multistep-form-group">
            <label><Users className="w-4 h-4" /> {t('tournamentType', 'Tournament Type')}</label>
            <div className="br-multistep-type-options">
              {typeOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={"br-multistep-type-option " + (formData.type === option.value ? 'selected' : '')}
                  onClick={() => setFormData({ ...formData, type: option.value })}
                >
                  <span className="type-icon">{option.icon}</span>
                  <span className="type-label">{option.label}</span>
                  <span className="type-desc">{option.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Number of Rounds */}
          <div className="br-multistep-form-group">
            <label><Gamepad2 className="w-4 h-4" /> {t('numberOfRounds', 'Number of Rounds')}</label>
            <div className="br-multistep-rounds-selector">
              {[2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  type="button"
                  className={"round-option " + (formData.totalRounds === num ? 'selected' : '')}
                  onClick={() => setFormData({ ...formData, totalRounds: num })}
                >
                  {num} {t('rounds', 'Rounds')}
                </button>
              ))}
            </div>
          </div>

          {/* Max Players */}
          <div className="br-multistep-form-group">
            <label><Users className="w-4 h-4" /> {t('maxPlayers', 'Max Players')}</label>
            <div className="br-multistep-input-wrapper">
              <input
                type="number"
                min={formData.type === 'solo' ? 2 : formData.type === 'duo' ? 4 : 8}
                max="100"
                step={getTeamSize()}
                value={formData.maxPlayers}
                onChange={(e) => { let val = parseInt(e.target.value) || 2; if (val > 100) val = 100; setFormData({ ...formData, maxPlayers: val }); }}
              />
              <span className="input-suffix">
                {formData.type === 'solo' ? t('players', 'Players') : (formData.maxPlayers + ' Players (' + getMaxTeams() + ' Teams)')}
              </span>
            </div>
          </div>

          {/* Prize Pool */}
          <div className="br-multistep-form-group">
            <label><Trophy className="w-4 h-4" /> {t('prizePool', 'Prize Pool')}</label>
            <div className="br-multistep-input-wrapper">
              <input
                type="number"
                min="0"
                max="1000000"
                value={formData.prizePool}
                onChange={(e) => setFormData({ ...formData, prizePool: parseInt(e.target.value) || 0 })}
              />
              <span className="input-suffix">{t('points', 'Points')}</span>
            </div>
          </div>

          {/* Control Mode */}
          <div className="br-multistep-form-group">
            <label><AlertCircle className="w-4 h-4" /> {t('controlMode', 'Control Mode')}</label>
            <div className="br-multistep-control-options">
              <button
                type="button"
                className={"control-option " + (formData.control === 'manual' ? 'selected' : '')}
                onClick={() => setFormData({ ...formData, control: 'manual' })}
              >
                <span className="control-label">{t('manual', 'Manual')}</span>
                <span className="control-desc">{t('manualDesc', 'Admin enters results')}</span>
              </button>
              <button
                type="button"
                className={"control-option " + (formData.control === 'bot' ? 'selected' : '')}
                onClick={() => setFormData({ ...formData, control: 'bot' })}
              >
                <span className="control-label">{t('bot', 'Bot')}</span>
                <span className="control-desc">{t('botDesc', 'Bot controlled')}</span>
              </button>
            </div>
          </div>

          {/* Password */}
          <div className="br-multistep-form-group">
            <label><Lock className="w-4 h-4" /> {t('password', 'Password')} ({t('optional', 'Optional')})</label>
            <input
              type="text"
              placeholder={t('leaveEmptyForPublic', 'Leave empty for public')}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          {/* Scheduled Start */}
          <div className="br-multistep-form-group">
            <label><Calendar className="w-4 h-4" /> {t('scheduledStart', 'Scheduled Start')} ({t('optional', 'Optional')})</label>
            <input
              type="datetime-local"
              value={formData.scheduledStart}
              onChange={(e) => setFormData({ ...formData, scheduledStart: e.target.value })}
              min={new Date().toISOString().slice(0, 16)}
            />
            <span className="form-hint">{t('scheduledStartHint', 'Let participants know when to be ready')}</span>
          </div>

          {/* Points Configuration */}
          <div className="br-multistep-form-group points-config">
            <label><Target className="w-4 h-4" /> {t('pointsConfiguration', 'Points Configuration')}</label>
            <div className="kill-points-row">
              <span>{t('pointsPerKill', 'Points per Kill')}:</span>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.pointsConfig.killPoints}
                onChange={(e) => setFormData({
                  ...formData,
                  pointsConfig: { ...formData.pointsConfig, killPoints: parseInt(e.target.value) || 0 }
                })}
              />
            </div>
            <div className="placement-points">
              <span className="placement-label">{t('placementPoints', 'Placement Points')}:</span>
              {formData.pointsConfig.topPlacements.map((placement, index) => (
                <div key={index} className="placement-row">
                  <span className="position-label">#{placement.position}</span>
                  <input
                    type="number"
                    min="0"
                    value={placement.points}
                    onChange={(e) => updatePlacement(index, 'points', e.target.value)}
                  />
                  <span className="pts-label">{t('pts', 'pts')}</span>
                  {formData.pointsConfig.topPlacements.length > 1 && (
                    <button type="button" className="remove-placement" onClick={() => removePlacement(index)}>
                      <Minus className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-placement" onClick={addPlacement}>
                <Plus className="w-4 h-4" />
                {t('addPlacement', 'Add Placement')}
              </button>
            </div>
          </div>

          {/* Cost Summary */}
          <div className="br-multistep-cost-summary">
            <h4>{t('tournamentSummary', 'Tournament Summary')}</h4>
            <div className="cost-row">
              <span>{t('type', 'Type')}</span>
              <span>{typeOptions.find(o => o.value === formData.type)?.label}</span>
            </div>
            <div className="cost-row">
              <span>{t('rounds', 'Rounds')}</span>
              <span>{formData.totalRounds}</span>
            </div>
            <div className="cost-row">
              <span>{t('maxPlayers', 'Max Players')}</span>
              <span>{formData.maxPlayers}{formData.type !== 'solo' && ' (' + getMaxTeams() + ' teams)'}</span>
            </div>
            <div className="cost-row prize">
              <span>{t('prizePool', 'Prize Pool')}</span>
              <span>{formData.prizePool} {t('pts', 'pts')}</span>
            </div>
            <div className="cost-row free-note">
              <span>✓ {t('freeForAdmins', 'Free to create for admins')}</span>
            </div>
          </div>

          <button type="submit" className="br-multistep-submit-btn" disabled={loading}>
            {loading ? t('creating', 'Creating...') : t('createTournament', 'Create Tournament')}
          </button>
        </form>

        {showConfirm && (
          <div className="br-multistep-confirm-overlay">
            <div className="br-multistep-confirm-modal">
              <h3>{t('confirmCreate', 'Confirm Tournament Creation')}</h3>
              <div className="confirm-details">
                <p><strong>{t('type', 'Type')}:</strong> {typeOptions.find(o => o.value === formData.type)?.label}</p>
                <p><strong>{t('rounds', 'Rounds')}:</strong> {formData.totalRounds}</p>
                <p><strong>{t('maxPlayers', 'Max Players')}:</strong> {formData.maxPlayers}</p>
                <p><strong>{t('prizePool', 'Prize Pool')}:</strong> {formData.prizePool} {t('pts', 'pts')}</p>
                <p><strong>{t('control', 'Control')}:</strong> {formData.control === 'manual' ? t('manual', 'Manual') : t('bot', 'Bot')}</p>
                <p className="free-badge">✓ {t('freeForAdmins', 'Free to create')}</p>
              </div>
              <div className="confirm-actions">
                <button className="confirm-btn confirm-yes" onClick={confirmCreate} disabled={loading}>
                  {loading ? t('creating', 'Creating...') : t('confirm', 'Confirm')}
                </button>
                <button className="confirm-btn confirm-no" onClick={() => setShowConfirm(false)} disabled={loading}>
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BRMultistepCreate;
